import { memo, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";

import {
  keys,
  useCalendar,
  useCreateCalendarJob,
  useExpenses,
  usePlanner,
  useSetDays,
  useSyncCalendar,
} from "../api/hooks";
import type { CalendarJob, Counts, DayKind, Planner, PlannerDay, Status } from "../api/types";
import { errorText, useFlash } from "../components/Flash";
import { LoadError, Loading, useYear, Stale } from "../components/Layout";
import { DayPie } from "../components/DayPie";
import { dlong, money, num, todayISO } from "../lib/format";
import { PHONE, useMediaQuery } from "../lib/useMediaQuery";

// "calendar" hands days back to the work calendar (or clears them, with no calendar): right-click, or on phones
// its own button.
type Tool = Status | "calendar";

/** An event title without its trailing dash: "Rig - NFL Paris - " -> "Rig - NFL Paris". */
const eventTitle = (s: string) => s.replace(/\s*-\s*$/, "");

export function PlannerPage() {
  const year = useYear();
  const q = usePlanner(year);
  if (q.isPending) return <Loading />;
  if (q.error) return <LoadError error={q.error} />;
  return (
    <Stale on={q.isPlaceholderData}>
      <PlannerView data={q.data} />
    </Stale>
  );
}

function PlannerView({ data }: { data: Planner }) {
  const { year, summary: s } = data;
  const expenses = useExpenses();
  const [tool, setTool] = useState<Tool>(data.statuses[0].key);
  // Days painted but not yet confirmed by the server: date -> new status ("" = cleared).
  const [pending, setPending] = useState<Map<string, DayKind>>(new Map());
  const painting = useRef<Map<string, DayKind> | null>(null);
  const stroke = useRef<Tool>(tool); // what the current stroke paints: the tool, or "calendar" on a right-drag
  const rightDown = useRef(false); // a right-button stroke, so its context menu event isn't a second reset
  const downOnJob = useRef<{ id: number; moved: boolean } | null>(null);
  const [flashSkip, setFlashSkip] = useState<string | null>(null);
  // Touch screens mark a day on tap (a click), so a scroll that starts on a day doesn't mark it.
  const touched = useRef(false);
  const phone = useMediaQuery(PHONE);
  const [month, setMonth] = useState(() => {
    const t = todayISO();
    return t.startsWith(String(year)) ? Number(t.slice(5, 7)) - 1 : 0;
  });
  const setDays = useSetDays(year);
  const flash = useFlash();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const today = todayISO();
  const statusName = useMemo<Record<string, string>>(
    () => ({
      ...Object.fromEntries(data.statuses.map((x) => [x.key, x.name])),
      onsite: "On site (no job yet)",
    }),
    [data.statuses],
  );
  const dayByDate = useMemo(
    () => new Map(data.months.flatMap((m) => m.days).flatMap((d) => (d ? [[d.date, d] as const] : []))),
    [data.months],
  );

  const dayAt = (el: Element | null): PlannerDay | undefined => {
    const btn = el?.closest<HTMLElement>("[data-date]");
    if (!btn) return undefined;
    const [m, d] = [Number(btn.dataset.m), Number(btn.dataset.d)];
    return data.months[m]?.days[d] ?? undefined;
  };

  const paint = (day: PlannerDay) => {
    if (day.job_id) {
      setFlashSkip(day.date);
      return;
    }
    const batch = painting.current;
    if (!batch) return;
    const used = stroke.current;
    const next: DayKind = used === "calendar" ? (day.calendar_kind ?? "") : used;
    batch.set(day.date, next);
    setPending((p) => new Map(p).set(day.date, next));
  };

  const commit = () => {
    const batch = painting.current;
    painting.current = null;
    const used = stroke.current;
    if (!batch || batch.size === 0) return;
    const dates = [...batch.keys()];
    const forget = () =>
      setPending((p) => {
        const n = new Map(p);
        dates.forEach((d) => n.delete(d));
        return n;
      });
    setDays.mutate(
      { dates, status: used },
      {
        onSuccess: (res) => {
          const changed = new Set(res.changed);
          qc.setQueryData<Planner>(
            keys.planner(year),
            (p) =>
              p && {
                ...p,
                months: p.months.map((m) => ({
                  ...m,
                  days: m.days.map((d) =>
                    d && changed.has(d.date)
                      ? {
                          ...d,
                          status: batch.get(d.date) ?? "",
                          source: used !== "calendar" ? "manual" : d.calendar_kind ? "calendar" : null,
                        }
                      : d,
                  ),
                })),
              },
          );
          forget();
        },
        onError: (err) => {
          forget();
          flash(`${errorText(err)} Your last change was undone.`, "error");
        },
      },
    );
  };

  const onPointerDown = (e: ReactPointerEvent) => {
    touched.current = e.pointerType !== "mouse";
    if (touched.current) return; // handled as a tap in onClick
    const day = dayAt(e.target as Element);
    if (!day || (e.button !== 0 && e.button !== 2)) return;
    rightDown.current = e.button === 2;
    if (day.job_id) {
      if (e.button === 0) downOnJob.current = { id: day.job_id, moved: false };
      return;
    }
    downOnJob.current = null;
    stroke.current = e.button === 2 ? "calendar" : tool;
    painting.current = new Map();
    paint(day);
    if (e.pointerType === "mouse") e.preventDefault();
  };

  // The row and column under the mouse are tinted by two bands laid over the wall, moved here rather than
  // re-rendering the days, so it's easy to see a day's date and month.
  const rowBand = useRef<HTMLDivElement>(null);
  const colBand = useRef<HTMLDivElement>(null);
  const crosshair = (el: Element | null) => {
    const [row, col] = [rowBand.current, colBand.current];
    if (!row || !col) return;
    const cell = el?.closest<HTMLElement>("[data-date]");
    row.hidden = col.hidden = !cell;
    if (!cell) return;
    row.style.top = `${cell.offsetTop}px`;
    row.style.height = `${cell.offsetHeight}px`;
    col.style.left = `${cell.offsetLeft}px`;
    col.style.width = `${cell.offsetWidth}px`;
  };

  const onPointerOver = (e: ReactPointerEvent) => {
    if (e.pointerType !== "mouse") return;
    crosshair(e.target as Element);
    if (downOnJob.current) downOnJob.current.moved = true;
    if (!painting.current) return;
    const day = dayAt(e.target as Element);
    if (day) paint(day);
  };

  // Finish a drag wherever the pointer is released.
  useEffect(() => {
    const up = () => {
      const j = downOnJob.current;
      downOnJob.current = null;
      if (j && !j.moved) navigate(`/job/${j.id}`);
      if (painting.current) commit();
    };
    window.addEventListener("pointerup", up);
    return () => window.removeEventListener("pointerup", up);
  });

  // Keyboard (Enter/Space on a focused day: a click with detail 0) and taps on touch screens mark one day.
  const onClick = (e: React.MouseEvent) => {
    if (e.detail !== 0 && !touched.current) return;
    touched.current = false;
    const day = dayAt(e.target as Element);
    if (!day) return;
    if (day.job_id) return navigate(`/job/${day.job_id}`);
    stroke.current = tool;
    painting.current = new Map();
    paint(day);
    commit();
  };

  // Right-click hands a day back to the calendar. A mouse right-click already did that in onPointerDown; this
  // catches the rest: the keyboard's menu key (or Shift+F10) and a long press on Android.
  const onContextMenu = (e: React.MouseEvent) => {
    const day = dayAt(e.target as Element);
    if (!day) return;
    e.preventDefault();
    if (rightDown.current) {
      rightDown.current = false;
      return;
    }
    touched.current = false;
    if (day.job_id) return;
    stroke.current = "calendar";
    painting.current = new Map();
    paint(day);
    commit();
  };

  // A tool this year or screen doesn't offer (quarantine from 2026; "From calendar" off phones) falls back.
  const offered = tool === "calendar" ? phone : data.statuses.some((st) => st.key === tool);
  useEffect(() => {
    if (!offered) setTool(data.statuses[0].key);
  }, [offered, data.statuses]);

  useEffect(() => {
    if (!flashSkip) return;
    const t = window.setTimeout(() => setFlashSkip(null), 600);
    return () => window.clearTimeout(t);
  }, [flashSkip]);

  // Optimistic counts for the pie/legend while a change is in flight.
  const counts: Counts = { ...s.counts };
  pending.forEach((next, date) => {
    const day = dayByDate.get(date);
    const prev = day?.status || "remaining";
    const to = next || "remaining";
    if (prev in counts) counts[prev as keyof Counts]--;
    if (to in counts) counts[to as keyof Counts]++;
  });

  return (
    <>
      <div className="page-head">
        <h1 id="planner-h">Planner, {year}</h1>
      </div>
      <div className="planner-layout">
        <section aria-labelledby="planner-h">
          <div className="paint" role="toolbar" aria-label="Day type">
            <span className="label">Mark days as</span>
            {data.statuses.map((st) => (
              <button
                key={st.key}
                type="button"
                className="swatch"
                aria-pressed={tool === st.key}
                onClick={() => setTool(st.key)}
              >
                <i className={`key-${st.key}`} />
                {st.name}
              </button>
            ))}
            {phone && (
              <button
                type="button"
                className="swatch"
                aria-pressed={tool === "calendar"}
                onClick={() => setTool("calendar")}
                title={data.calendar.url ? "Go back to what the work calendar says" : "Remove the mark"}
              >
                <i className={data.calendar.url ? "key-onsite from-cal" : "key-remaining"} />
                {data.calendar.url ? "From calendar" : "Clear"}
              </button>
            )}
            <span className="hint">
              {phone
                ? "Tap a day to mark it."
                : `Click or drag across days; right-click to ${data.calendar.url ? "go back to the calendar" : "clear"}.`}{" "}
              Orange job days come from <Link to={`/year/${year}/jobs`}>Jobs</Link>.
            </span>
          </div>

          {phone && (
            <MonthView
              data={data}
              month={month}
              onMonth={(m) => {
                if (m < 0 || m > 11) {
                  setMonth(m < 0 ? 11 : 0);
                  navigate(`/year/${year + (m < 0 ? -1 : 1)}`);
                } else setMonth(m);
              }}
              pending={pending}
              today={today}
              skip={flashSkip}
              names={statusName}
              handlers={{ onPointerDown, onPointerOver, onClick, onContextMenu }}
            />
          )}
          <div className="wall" hidden={phone}>
            <div
              className="wall-grid"
              onPointerDown={onPointerDown}
              onPointerOver={onPointerOver}
              onPointerLeave={() => crosshair(null)}
              onClick={onClick}
              onContextMenu={onContextMenu}
            >
              <div className="wall-band row" ref={rowBand} hidden aria-hidden="true" />
              <div className="wall-band col" ref={colBand} hidden aria-hidden="true" />
              <div className="mh" aria-hidden="true" />
              {data.months.map((m) => (
                <div key={m.short} className="mh">
                  {m.short}
                </div>
              ))}
              {Array.from({ length: 31 }, (_, i) => (
                <Row
                  key={i}
                  i={i}
                  data={data}
                  pending={pending}
                  today={today}
                  skip={flashSkip}
                  names={statusName}
                />
              ))}
            </div>
          </div>
          <p className="small muted" style={{ marginTop: ".5rem" }}>
            Blue corner = bank holiday. {phone ? "Circled" : "Outlined"} = today.
            {data.calendar.url && " Striped = from your work calendar."}
          </p>
        </section>

        <aside className="side" aria-label="Year summary">
          <div className="panel">
            <h2>DOILs</h2>
            <div className="figure">
              <span className="big">{num(s.doil_now)}</span>
              <span className="what muted">balance today</span>
            </div>
            <dl className="rows">
              <dt>Carried in from {year - 1}</dt>
              <dd>{num(s.doil_start)}</dd>
              <dt>Earned so far</dt>
              <dd>{num(s.doil_so_far)}</dd>
              <dt>Earned by year end (booked)</dt>
              <dd>{num(s.doil_accrued)}</dd>
              <dt>Used</dt>
              <dd>{num(s.doil_used)}</dd>
              <dt>Sold</dt>
              <dd>{num(s.doil_sold)}</dd>
              <dt className="total">Balance at year end</dt>
              <dd className="total">{num(s.doil_end)}</dd>
            </dl>
            <p className="small" style={{ margin: ".6rem 0 0" }}>
              <Link to={`/year/${year}/doils`}>See each fortnight</Link>
            </p>
          </div>

          <div className="panel">
            <h2>Holidays</h2>
            <div className="figure">
              <span className="big">{num(s.holiday_remaining)}</span>
              <span className="what muted">days left</span>
            </div>
            <dl className="rows">
              <dt>Allowance</dt>
              <dd>{num(s.holiday_allowance)}</dd>
              <dt>Carried in</dt>
              <dd>{num(s.holiday_carry)}</dd>
              <dt>Taken</dt>
              <dd>{num(s.holiday_taken)}</dd>
            </dl>
          </div>

          <CalendarPanel data={data} />

          <div className="panel">
            <h2>Days</h2>
            <div className="pie-wrap">
              <DayPie counts={counts} names={statusName} />
            </div>
            <ul className="legend">
              <li>
                <i className="key-job" />
                On job<b>{counts.job}</b>
              </li>
              {counts.onsite > 0 && (
                <li>
                  <i className="key-onsite from-cal" />
                  On site (no job yet)<b>{counts.onsite}</b>
                </li>
              )}
              {data.statuses.map((st) => (
                <li key={st.key}>
                  <i className={`key-${st.key}`} />
                  {st.name}
                  <b>{counts[st.key]}</b>
                </li>
              ))}
              <li>
                <i className="key-remaining" />
                Not marked<b>{counts.remaining}</b>
              </li>
            </ul>
            <dl className="rows">
              <dt>Days worked (job + base{s.counts.onsite ? " + on site" : ""})</dt>
              <dd>{s.counts.worked}</dd>
              <dt>Days not at work</dt>
              <dd>{s.counts.not_at_work}</dd>
            </dl>
          </div>

          <div className="panel">
            <h2>Roles</h2>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Role</th>
                    <th className="num">Jobs</th>
                    <th className="num">Days</th>
                  </tr>
                </thead>
                <tbody>
                  {data.roles.map((r) => (
                    <tr key={r.code || "none"}>
                      <td>{r.name}</td>
                      <td className="num">{r.jobs}</td>
                      <td className="num">{r.days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {expenses && (
              <dl className="rows" style={{ marginTop: ".8rem" }}>
                <dt>Expenses this year</dt>
                <dd>{money(data.totals.total)}</dd>
                <dt>Miles</dt>
                <dd>{num(data.totals.mileage)}</dd>
              </dl>
            )}
          </div>
        </aside>
      </div>
    </>
  );
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface MonthViewProps {
  data: Planner;
  month: number; // 0-11
  onMonth: (m: number) => void; // -1 and 12 move into the next or previous year
  pending: Map<string, DayKind>;
  today: string;
  skip: string | null;
  names: Record<string, string>;
  handlers: {
    onPointerDown: (e: ReactPointerEvent) => void;
    onPointerOver: (e: ReactPointerEvent) => void;
    onClick: (e: React.MouseEvent) => void;
    onContextMenu: (e: React.MouseEvent) => void;
  };
}

/** Phones: one month as a calendar, Monday first. Arrows or a sideways swipe change month. */
function MonthView({ data, month, onMonth, pending, today, skip, names, handlers }: MonthViewProps) {
  const m = data.months[month];
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const days = m.days.filter((d): d is PlannerDay => d !== null);
  const lead = (new Date(`${days[0].date}T00:00:00Z`).getUTCDay() + 6) % 7; // blanks before the 1st
  const s = data.summary;
  // A job's name is written once per run of days (and again each Monday), across the run like an event bar.
  const tagOf = (d: PlannerDay) =>
    d.job_name ?? (d.status === "onsite" && d.calendar ? eventTitle(d.calendar) : null);
  const spans = days.map((d, i) => {
    const t = tagOf(d);
    if (!t || (i > 0 && (lead + i) % 7 !== 0 && tagOf(days[i - 1]) === t)) return 0;
    let n = 1;
    while (i + n < days.length && (lead + i + n) % 7 !== 0 && tagOf(days[i + n]) === t) n++;
    return n;
  });
  return (
    <div className="month-view">
      <div className="mv-summary">
        <span>
          <b>{num(s.doil_now)}</b> DOILs today
        </span>
        <span>
          <b>{num(s.holiday_remaining)}</b> holiday days left
        </span>
      </div>
      <div className="mv-head">
        <button
          type="button"
          className="mv-arrow"
          aria-label="Previous month"
          onClick={() => onMonth(month - 1)}
        >
          ‹
        </button>
        <h2>
          {m.name} <span className="muted">{data.year}</span>
        </h2>
        <button type="button" className="mv-arrow" aria-label="Next month" onClick={() => onMonth(month + 1)}>
          ›
        </button>
      </div>
      <div
        className="mv-grid"
        onPointerDown={handlers.onPointerDown}
        onPointerOver={handlers.onPointerOver}
        onClick={handlers.onClick}
        onContextMenu={handlers.onContextMenu}
        onTouchStart={(e) => (swipe.current = { x: e.touches[0].clientX, y: e.touches[0].clientY })}
        onTouchEnd={(e) => {
          const start = swipe.current;
          swipe.current = null;
          if (!start) return;
          const dx = e.changedTouches[0].clientX - start.x;
          const dy = e.changedTouches[0].clientY - start.y;
          if (Math.abs(dx) > 60 && Math.abs(dx) > 2 * Math.abs(dy)) onMonth(month + (dx < 0 ? 1 : -1));
        }}
      >
        {WEEKDAYS.map((w) => (
          <div key={w} className="mv-wd" aria-hidden="true">
            {w.slice(0, 1)}
          </div>
        ))}
        {Array.from({ length: lead }, (_, i) => (
          <div key={`b${i}`} className="mv-blank" aria-hidden="true" />
        ))}
        {days.map((d, i) => (
          <MonthDay
            key={d.date}
            d={d}
            mi={month}
            i={i}
            pending={pending.get(d.date)}
            isToday={d.date === today}
            flash={skip === d.date}
            names={names}
            span={spans[i]}
          />
        ))}
      </div>
    </div>
  );
}

const MonthDay = memo(function MonthDay({
  d,
  mi,
  i,
  pending,
  isToday,
  flash,
  names,
  span,
}: DayCellProps & { span: number }) {
  const status = pending ?? d.status;
  const tag = span ? (d.job_name ?? (d.calendar ? eventTitle(d.calendar) : null)) : null;
  return (
    <button
      type="button"
      className={dayClass(d, status, pending, isToday, flash, "mv-day")}
      data-date={d.date}
      data-m={mi}
      data-d={i}
      aria-label={dayLabel(d, status, names)}
    >
      <span className="mv-num">{d.day}</span>
      {tag ? (
        <span className="mv-tag" style={{ "--span": span } as React.CSSProperties}>
          {tag}
        </span>
      ) : d.bank_holiday && !d.job_name ? (
        <span className="mv-tag">BH</span>
      ) : null}
    </button>
  );
});

function dayClass(
  d: PlannerDay,
  status: DayKind,
  pending: DayKind | undefined,
  isToday: boolean,
  flash: boolean,
  base = "day",
): string {
  return [
    base,
    status && `s-${status}`,
    d.weekend && "weekend",
    d.bank_holiday && "bh",
    isToday && "today",
    pending === undefined && d.source === "calendar" && "from-cal",
    flash && "flash-skip",
  ]
    .filter(Boolean)
    .join(" ");
}

function dayLabel(d: PlannerDay, status: DayKind, names: Record<string, string>): string {
  return (
    dlong(d.date) +
    (d.bank_holiday ? `, ${d.bank_holiday}` : "") +
    (d.job_name ? `, job: ${d.job_name}` : status ? `, ${names[status] ?? ""}` : "") +
    (d.calendar ? `, work calendar: ${eventTitle(d.calendar)}` : "")
  );
}

interface RowProps {
  i: number;
  data: Planner;
  pending: Map<string, DayKind>;
  today: string;
  skip: string | null;
  names: Record<string, string>;
}

function Row({ i, data, pending, today, skip, names }: RowProps) {
  return (
    <>
      <div className="rn" aria-hidden="true">
        {i + 1}
      </div>
      {data.months.map((m, mi) => {
        const d = m.days[i];
        if (!d) return <div key={m.short} className="blank" aria-hidden="true" />;
        return (
          <DayCell
            key={m.short}
            d={d}
            mi={mi}
            i={i}
            pending={pending.get(d.date)}
            isToday={d.date === today}
            flash={skip === d.date}
            names={names}
          />
        );
      })}
    </>
  );
}

interface DayCellProps {
  d: PlannerDay;
  mi: number;
  i: number;
  pending: DayKind | undefined; // painted but not yet saved
  isToday: boolean;
  flash: boolean;
  names: Record<string, string>;
}

// Memoised: while dragging a paint stroke only the days whose props change re-render, not all 372.
const DayCell = memo(function DayCell({ d, mi, i, pending, isToday, flash, names }: DayCellProps) {
  const status = pending ?? d.status;
  const label = dayLabel(d, status, names);
  return (
    <button
      type="button"
      className={dayClass(d, status, pending, isToday, flash)}
      data-date={d.date}
      data-m={mi}
      data-d={i}
      title={
        dlong(d.date) +
        (d.bank_holiday ? ` – ${d.bank_holiday}` : "") +
        (d.job_name ? ` – ${d.job_name}` : "") +
        (d.calendar ? `\nWork calendar: ${eventTitle(d.calendar)}` : "")
      }
      aria-label={label}
    >
      <span className="wd">{d.weekday}</span>
      {d.job_name ? (
        <span className="tag">{d.job_name}</span>
      ) : status === "onsite" && d.calendar ? (
        <span className="tag">{eventTitle(d.calendar)}</span>
      ) : d.bank_holiday ? (
        <span className="tag muted">BH</span>
      ) : null}
    </button>
  );
});
const SHOWN = 5; // calendar jobs listed before "Show more"

/** Work calendar: when it last synced, and its job days with no job yet, each with a button to create the job. */
function CalendarPanel({ data }: { data: Planner }) {
  const { calendar_jobs: jobs, year } = data;
  const live = useCalendar(data.calendar);
  const calendar = live.data ?? data.calendar;
  const create = useCreateCalendarJob();
  const sync = useSyncCalendar();
  const expenses = useExpenses();
  const flash = useFlash();
  const navigate = useNavigate();
  const today = todayISO();
  // After "Update now", say how it went once the (slow) read finishes.
  const [asked, setAsked] = useState(false);
  const [all, setAll] = useState(false);
  useEffect(() => {
    if (!asked || calendar.syncing) return;
    setAsked(false);
    flash(calendar.error ?? "Calendar updated.", calendar.error ? "error" : "ok");
  }, [asked, calendar.syncing, calendar.error, flash]);
  if (!calendar.url) return null;

  const make = (j: CalendarJob) =>
    create.mutate(j, {
      onSuccess: (job) => {
        flash(expenses ? `Created “${job.name}”. Add its expenses here.` : `Created “${job.name}”.`);
        navigate(`/job/${job.id}`);
      },
      onError: (err) => flash(errorText(err), "error"),
    });
  const synced = calendar.synced_at
    ? new Date(calendar.synced_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })
    : null;

  return (
    <div className="panel">
      <h2>Work calendar</h2>
      {calendar.error && <p className="error-text small">{calendar.error}</p>}
      {jobs.length ? (
        <>
          <p className="small muted" style={{ margin: "0 0 .5rem" }}>
            Job days with no job yet.
            {expenses
              ? " Create one to add its expenses."
              : " Ones still to come become jobs by themselves once they've finished, or create one now."}
          </p>
          <ul className="cal-jobs">
            {(all ? jobs : jobs.slice(0, SHOWN)).map((j) => (
              <li key={j.start} className={j.end < today ? "is-past" : undefined}>
                <div>
                  <b>{j.name}</b>
                  <span className="muted small">
                    {j.start === j.end ? dlong(j.start) : `${dlong(j.start)} – ${dlong(j.end)}`} ·{" "}
                    {j.dates.length} {j.dates.length === 1 ? "day" : "days"}
                    {j.location ? ` · ${j.location}` : ""}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn small quiet"
                  disabled={create.isPending}
                  onClick={() => make(j)}
                  aria-label={`Create job: ${j.name}, ${dlong(j.start)}`}
                >
                  Create job
                </button>
              </li>
            ))}
          </ul>
          {jobs.length > SHOWN && (
            <button
              type="button"
              className="linkish small"
              style={{ marginTop: ".5rem" }}
              onClick={() => setAll(!all)}
            >
              {all ? "Show fewer" : `Show ${jobs.length - SHOWN} more`}
            </button>
          )}
        </>
      ) : (
        <p className="small muted">Every job day on the calendar in {year} has a job.</p>
      )}
      <p className="small muted" style={{ margin: ".6rem 0 0" }}>
        {calendar.syncing ? (
          <span role="status">Reading the calendar… this can take a minute.</span>
        ) : (
          <>
            {synced ? `Updated ${synced}.` : "Not read yet."}{" "}
            <button
              type="button"
              className="linkish"
              disabled={sync.isPending}
              onClick={() =>
                sync.mutate(undefined, {
                  onSuccess: () => setAsked(true),
                  onError: (err) => flash(errorText(err), "error"),
                })
              }
            >
              Update now
            </button>
          </>
        )}
      </p>
    </div>
  );
}
